/**
 * Особисті налаштування інтерфейсу (PRF-05, UX-45, UX-36; формат — E3a-05).
 *
 * Формат зберігання — один ключ `cl:prefs` у localStorage, JSON-об'єкт `{ v: 1, <поле>: … }`.
 * Опис полів — `PREFS_SCHEMA`: типове значення, перевірка, область. Правила (web-shell.md, розд. 9):
 * - нове поле — рядок у `Prefs` і в `PREFS_SCHEMA`; версію не підвищують: старі записи просто
 *   отримують типове значення;
 * - поле, яке змінило зміст чи форму, — нова версія `PREFS_VERSION` і крок у `migrate()`;
 * - невідоме чи зіпсоване значення поля дає типове (сусідні поля від цього не страждають); невідомі
 *   ключі зберігаються як є — вкладка новішої версії не втрачає своїх полів через стару;
 * - атрибути `<html>` до першого малювання ставить `public/boot.js`: він читає ті самі поля з тими
 *   самими перевірками (тест `prefs.test.ts` звіряє).
 *
 * Область поля: `person` — вибір людини (зберігається й на сервері, `GET/PATCH /api/v1/me/preferences`,
 * синхронізацію веде `sync.ts`; тут — кеш для старту без блимання); `device` — лише цей пристрій
 * (легкий режим: різні ПК людини можуть мати різний режим, lite-mode.md, розд. 2).
 * Це налаштування, а не персональні дані.
 */
import { RECORD_SECTION_ENTRY, RECORD_SECTIONS_MAX } from '@clientline/contracts/record-sections';
import { useSyncExternalStore } from 'react';

export type Theme = 'system' | 'light' | 'dark';
export type Density = 'comfortable' | 'compact';
/** pending — ще не пропонували; later — показано чи «Не зараз» (знову через 7 днів); never — назавжди. */
export type LiteOffer = 'pending' | 'later' | 'never';
/** Вигляд лівої навігації: `auto` — за шириною вікна (layout.md, розд. 3); `rail` і `expanded` закріплені. */
export type NavMode = 'auto' | 'rail' | 'expanded';
/** Перший день тижня (PRF-03): понеділок, субота чи неділя. */
export type WeekStart = 'mon' | 'sat' | 'sun';

export interface Prefs {
  theme: Theme;
  density: Density;
  lite: boolean;
  liteOffer: LiteOffer;
  /** Коли пропозицію легкого режиму показали чи відклали (мс epoch); 0 — ще ні. */
  liteOfferAt: number;
  todayOpen: boolean;
  /** Однолітерні гарячі клавіші (WCAG 2.1.4, PRF-08). */
  singleKeys: boolean;
  /** Позначки клавіш у кнопках, меню й палітрі (PRF-08). */
  keyHints: boolean;
  nav: NavMode;
  /**
   * Стартова сторінка (PRF-06): маршрут застосунку з профілю людини або типовий для її ролі; `''` —
   * як у `/meta` (`start`). Кеш значення сервера: «/» переадресовує без очікування запиту.
   */
  startPage: string;
  /** Перший день тижня (PRF-03): значення людини, інакше компанії (`/me/preferences`, `defaults`). */
  weekStart: WeekStart;
  /**
   * Кеш чинного часового поясу людини (PRF-04): її пояс із `GET /me`, інакше пояс компанії. Не
   * синхронізується (мову й пояс людина змінює через `PATCH /me`, E3a-11): його пише лише
   * `i18n/region.ts` після відповіді сервера, щоб перший кадр уже мав правильні години. `''` — ще
   * не знаємо, діє Europe/Kyiv.
   */
  timeZone: string;
  /** «Більше не питати для цього пристрою» про розбіжність поясу пристрою і профілю (PRF-04). */
  tzHintOff: boolean;
  /**
   * Не показувати привітання першого входу на цьому пристрої (UX-38; e2e, розгортання з власним
   * навчанням). Це вимикач, а не позначка «показано»: ту веде сервер (`/me/preferences`, `welcomed`),
   * бо пристрій не тримає даних людини.
   */
  welcomeOff: boolean;
  /**
   * Особистий стан згортання секцій картки (REC-03): `<сутність>/<секція>=1` — розгорнута,
   * `=0` — згорнута; лише те, що людина змінила сама. Найновіші — в кінці, понад
   * `RECORD_SECTIONS_MAX` найстаріші відкидаються. Серверне збереження (на всіх ПК людини) — тікет
   * cb2244df: `/me/preferences` такого поля ще не має.
   */
  recordSections: readonly string[];
  /**
   * Тип останнього швидкого створення (DAT-01): у діалозі він перший і обраний. Ключ сутності, не
   * дані людини, тож поле пристрою; немає такої сутності чи права — діалог бере перший тип.
   */
  quickCreateLast: string;
  /**
   * Особистий вибір і порядок стовпців подання (LST-02): `<подання>=<поле>,<поле>` — без поля-назви,
   * яке завжди перше. Лише ключі полів, без значень. Найновіші — в кінці, понад `LIST_COLUMNS_MAX`
   * найстаріші відкидаються. ЗАСТАРІЛЕ (E4a-03): вибір тепер — особистий шар подання на сервері
   * (`PUT /views/{id}/prefs`, platform/view-prefs.ts). Поле лише читається й прибирається після
   * одноразового перенесення; нових записів немає. Поле прибрати — коли перенесення відпрацює.
   */
  listColumns: readonly string[];
  /**
   * Вигляд екрана сутності з воронкою (KAN-01): `<сутність>=board` чи `<сутність>=list` — останній
   * вибір у перемикачі «Воронка | Список». Лише ключі сутностей. Найновіші — в кінці, понад
   * `VIEW_LAYOUTS_MAX` найстаріші відкидаються. Поле людини: на сервер піде, коли `/me/preferences`
   * матиме таке поле (як `recordSections`); до того — на цьому пристрої, і «Вийти» його скидає.
   */
  viewLayouts: readonly string[];
  /**
   * Остання воронка дошки подання (KAN-26, особисте): `<подання>=<воронка>`. Лише ключі. Найновіші —
   * в кінці, понад `BOARD_PIPELINES_MAX` найстаріші відкидаються. Як `viewLayouts` — на цьому
   * пристрої, доки `/me/preferences` не матиме такого поля; «Вийти» його скидає.
   */
  boardPipelines: readonly string[];
  /**
   * Права панель картки на широкій області (> 1215 px; layout.md, розд. 7, правило 1а): відкрита чи
   * сховата. Вигляд цього пристрою, як `todayOpen`; на вузькій області панель накладна й стану не
   * пам'ятає (рішення власника 2026-10-27).
   */
  recordAsideOpen: boolean;
}

export const PREFS_KEY = 'cl:prefs';
/** Версія формату. v0 — записи S4 без поля `v`: ті самі поля, тож міграція порожня. */
export const PREFS_VERSION = 1;

export const WEEK_STARTS: readonly WeekStart[] = ['mon', 'sat', 'sun'];
export const THEMES: readonly Theme[] = ['system', 'light', 'dark'];
export const DENSITIES: readonly Density[] = ['comfortable', 'compact'];

interface Field<T> {
  default: T;
  /** Значення зі сховища → значення поля; `undefined` — зіпсоване, береться типове. */
  parse: (raw: unknown) => T | undefined;
  scope: 'person' | 'device';
}

const oneOf = <T extends string>(
  values: readonly T[],
  fallback: T,
  scope: Field<T>['scope'],
): Field<T> => ({
  default: fallback,
  parse: (raw) => ((values as readonly unknown[]).includes(raw) ? (raw as T) : undefined),
  scope,
});
const flag = (fallback: boolean, scope: Field<boolean>['scope']): Field<boolean> => ({
  default: fallback,
  parse: (raw) => (typeof raw === 'boolean' ? raw : undefined),
  scope,
});
/** Внутрішній шлях застосунку (`/l/x`); перевірку на чужий хост і права робить `home.tsx`. */
const path = (scope: Field<string>['scope']): Field<string> => ({
  default: '',
  parse: (raw) =>
    typeof raw === 'string' && (raw === '' || /^\/(?!\/)[^\s\\]{0,511}$/.test(raw))
      ? raw
      : undefined,
  scope,
});
/** Назва поясу IANA (форма; чи знає її рушій, перевіряє `format.ts`). Порожньо — «не знаємо». */
const ZONE = /^[A-Za-z][A-Za-z0-9_+-]*(\/[A-Za-z0-9_+-]+){0,2}$/;
const zone = (scope: Field<string>['scope']): Field<string> => ({
  default: '',
  parse: (raw) =>
    typeof raw === 'string' && (raw === '' || (raw.length <= 64 && ZONE.test(raw)))
      ? raw
      : undefined,
  scope,
});

/** Межа й форма запису — одне джерело з `/me/preferences` (`@clientline/contracts/record-sections`). */
export { RECORD_SECTION_ENTRY, RECORD_SECTIONS_MAX };

const sections = (scope: Field<readonly string[]>['scope']): Field<readonly string[]> => ({
  default: [],
  parse: (raw) =>
    Array.isArray(raw) &&
    raw.length <= RECORD_SECTIONS_MAX &&
    raw.every((x) => typeof x === 'string' && RECORD_SECTION_ENTRY.test(x))
      ? (raw as string[])
      : undefined,
  scope,
});
/** Ключ сутності (канонічне фізичне ім'я, metadata-ui.md, розд. 1); порожньо — не обирали. */
const entityKey = (scope: Field<string>['scope']): Field<string> => ({
  default: '',
  parse: (raw) =>
    typeof raw === 'string' && (raw === '' || /^[a-z][a-z0-9_]{0,62}$/.test(raw)) ? raw : undefined,
  scope,
});
/** Межа подань з особистими стовпцями: ~20 сутностей × кілька подань, ≤ 16 КБ у сховищі. */
export const LIST_COLUMNS_MAX = 100;
/** Ключ подання з `/meta` (`<сутність>.<ключ>`) і до 30 ключів полів (VIEW_LIMITS.columns). */
export const LIST_COLUMNS_ENTRY =
  /^[a-z][a-z0-9_]{0,62}\.[a-z][a-z0-9_]{0,62}=(?:[a-z][a-z0-9_]{0,62}(?:,[a-z][a-z0-9_]{0,62}){0,29})?$/;
const columnLists = (scope: Field<readonly string[]>['scope']): Field<readonly string[]> => ({
  default: [],
  parse: (raw) =>
    Array.isArray(raw) &&
    raw.length <= LIST_COLUMNS_MAX &&
    raw.every((x) => typeof x === 'string' && LIST_COLUMNS_ENTRY.test(x))
      ? (raw as string[])
      : undefined,
  scope,
});
/** Межа записів вигляду: по одному на сутність з воронкою. */
export const VIEW_LAYOUTS_MAX = 50;
export const VIEW_LAYOUT_ENTRY = /^[a-z][a-z0-9_]{0,62}=(?:board|list)$/;
const layouts = (scope: Field<readonly string[]>['scope']): Field<readonly string[]> => ({
  default: [],
  parse: (raw) =>
    Array.isArray(raw) &&
    raw.length <= VIEW_LAYOUTS_MAX &&
    raw.every((x) => typeof x === 'string' && VIEW_LAYOUT_ENTRY.test(x))
      ? (raw as string[])
      : undefined,
  scope,
});
export const BOARD_PIPELINES_MAX = 50;
export const BOARD_PIPELINE_ENTRY = /^[a-z0-9_.-]{1,130}=[a-z][a-z0-9_]{0,62}$/;
const pipelineEntries = (scope: Field<readonly string[]>['scope']): Field<readonly string[]> => ({
  default: [],
  parse: (raw) =>
    Array.isArray(raw) &&
    raw.length <= BOARD_PIPELINES_MAX &&
    raw.every((x) => typeof x === 'string' && BOARD_PIPELINE_ENTRY.test(x))
      ? (raw as string[])
      : undefined,
  scope,
});
const time = (scope: Field<number>['scope']): Field<number> => ({
  default: 0,
  parse: (raw) => (typeof raw === 'number' && Number.isFinite(raw) && raw >= 0 ? raw : undefined),
  scope,
});

export const PREFS_SCHEMA: { readonly [K in keyof Prefs]: Field<Prefs[K]> } = {
  theme: oneOf(THEMES, 'system', 'person'),
  density: oneOf(DENSITIES, 'comfortable', 'person'),
  lite: flag(false, 'device'),
  liteOffer: oneOf<LiteOffer>(['pending', 'later', 'never'], 'pending', 'device'),
  liteOfferAt: time('device'),
  todayOpen: flag(true, 'device'),
  singleKeys: flag(true, 'person'),
  keyHints: flag(true, 'person'),
  nav: oneOf<NavMode>(['auto', 'rail', 'expanded'], 'auto', 'device'),
  startPage: path('person'),
  weekStart: oneOf<WeekStart>(WEEK_STARTS, 'mon', 'person'),
  timeZone: zone('person'),
  tzHintOff: flag(false, 'device'),
  welcomeOff: flag(false, 'device'),
  recordSections: sections('person'),
  quickCreateLast: entityKey('device'),
  listColumns: columnLists('person'),
  viewLayouts: layouts('person'),
  boardPipelines: pipelineEntries('person'),
  recordAsideOpen: flag(true, 'device'),
};

/** Поля людини: їх зберігає й сервер (prefs/sync.ts). */
export const PERSON_FIELDS = (Object.keys(PREFS_SCHEMA) as Array<keyof Prefs>).filter(
  (key) => PREFS_SCHEMA[key].scope === 'person',
);

const FIELDS = Object.keys(PREFS_SCHEMA) as Array<keyof Prefs>;

/**
 * Поля, що переживають «Вийти» (рішення Security-2, #388-хвіст): налаштування пристрою (`device`
 * без id людини) і вигляд першого кадру, який ставить `boot.js` до малювання (тема, щільність,
 * позначки клавіш). Решта — поля людини (`timeZone`, `startPage`, `weekStart`,
 * `singleKeys`) і невідомі ключі — на виході скидається до типових. Нове поле за замовчуванням
 * скидається; лишити його можна лише тут, явно (тест перебирає `PREFS_SCHEMA`).
 */
export const KEPT_ON_SIGN_OUT: ReadonlySet<keyof Prefs> = new Set<keyof Prefs>([
  'theme',
  'density',
  'keyHints',
  'lite',
  'liteOffer',
  'liteOfferAt',
  'todayOpen',
  'nav',
  'tzHintOff',
  'welcomeOff',
]);

export const defaultPrefs: Prefs = Object.fromEntries(
  FIELDS.map((key) => [key, PREFS_SCHEMA[key].default]),
) as unknown as Prefs;

type Stored = Record<string, unknown>;

/** Запис старішої версії → поточна. Кожен крок — `if (v < N) { …; v = N }`. */
function migrate(raw: Stored): Stored {
  let v = typeof raw.v === 'number' ? raw.v : 0;
  if (v < 1) v = 1; // v0 (S4) має ті самі поля
  return { ...raw, v };
}

/** Розібрати вміст ключа: будь-що зіпсоване дає типові значення, а не виняток. */
export function parsePrefs(text: string | null): { prefs: Prefs; extra: Stored } {
  let raw: unknown;
  try {
    raw = JSON.parse(text ?? '{}');
  } catch {
    raw = {};
  }
  const stored: Stored =
    raw && typeof raw === 'object' && !Array.isArray(raw) ? migrate(raw as Stored) : {};
  const prefs = {} as Record<keyof Prefs, unknown>;
  const extra: Stored = {};
  for (const [key, value] of Object.entries(stored)) {
    if (key !== 'v' && !(key in PREFS_SCHEMA)) extra[key] = value;
  }
  for (const key of FIELDS) {
    const field = PREFS_SCHEMA[key] as Field<unknown>;
    prefs[key] = field.parse(stored[key]) ?? field.default;
  }
  return { prefs: prefs as Prefs, extra };
}

export function serializePrefs(prefs: Prefs, extra: Stored = {}): string {
  return JSON.stringify({ ...extra, ...prefs, v: PREFS_VERSION });
}

function read() {
  try {
    return parsePrefs(localStorage.getItem(PREFS_KEY));
  } catch {
    return parsePrefs(null);
  }
}

let state = read();
const listeners = new Set<() => void>();

/**
 * Колір панелі браузера (`theme-color`) — `--cl-bg` теми. У `index.html` він і `color-scheme`
 * стоять за `prefers-color-scheme`; при темі, обраній явно, теги беруть її значення (і в boot.js).
 */
export const THEME_COLOR = { light: '#f3f4f6', dark: '#0f1115' } as const;

/** Атрибути на <html> і колір панелі браузера: те саме, що ставить public/boot.js. */
export function applyToDocument(p: Prefs, root: HTMLElement = document.documentElement) {
  if (p.theme === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', p.theme);
  if (p.density === 'compact') root.setAttribute('data-density', 'compact');
  else root.removeAttribute('data-density');
  if (p.lite) root.setAttribute('data-lite', 'on');
  else root.removeAttribute('data-lite');
  if (p.keyHints === false) root.setAttribute('data-key-hints', 'off');
  else root.removeAttribute('data-key-hints');
  root.ownerDocument
    .querySelector('meta[name="color-scheme"]')
    ?.setAttribute('content', p.theme === 'system' ? 'light dark' : p.theme);
  for (const meta of root.ownerDocument.querySelectorAll('meta[name="theme-color"]')) {
    const scheme = meta.getAttribute('media')?.includes('dark') ? 'dark' : 'light';
    meta.setAttribute('content', THEME_COLOR[p.theme === 'system' ? scheme : p.theme]);
  }
}

export function getPrefs(): Prefs {
  return state.prefs;
}

/** Зміна людиною (не з сервера): лише поля `person`, що справді змінились. */
const personListeners = new Set<(patch: Partial<Prefs>) => void>();

/** Підписка на зміни полів людини, зроблені в цій вкладці (меню, палітра, профіль). */
export function onPersonPrefs(listener: (patch: Partial<Prefs>) => void): () => void {
  personListeners.add(listener);
  return () => personListeners.delete(listener);
}

export function setPrefs(patch: Partial<Prefs>) {
  if (sealed) return;
  const changed: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(patch) as Array<[keyof Prefs, unknown]>) {
    if (PREFS_SCHEMA[key]?.scope === 'person' && state.prefs[key] !== value) changed[key] = value;
  }
  writePrefs(patch);
  if (Object.keys(changed).length === 0) return;
  for (const l of personListeners) l(changed as Partial<Prefs>);
}

/** Значення з сервера (prefs/sync.ts): лише кеш і `<html>`, без повторного запису на сервер. */
export function applyServerPrefs(patch: Partial<Prefs>) {
  if (sealed) return;
  const differs = (Object.keys(patch) as Array<keyof Prefs>).some(
    (key) => state.prefs[key] !== patch[key],
  );
  if (differs) writePrefs(patch);
}

/**
 * Після `resetPersonPrefs` у `cl:prefs` нічого не пишемо до кінця документа: запит у дорозі
 * (`sync.ts` на 401 викликає `applyToCache`) не поверне поля попередньої людини.
 */
let sealed = false;

/**
 * Вихід (і інша людина після повторного входу): скинути поля людини й невідомі ключі до типових,
 * а `KEPT_ON_SIGN_OUT` лишити; пам'ять і сховище разом. Далі `cl:prefs` не змінюється (засувка).
 */
export function resetPersonPrefs() {
  const next = { ...defaultPrefs } as Record<keyof Prefs, unknown>;
  for (const key of KEPT_ON_SIGN_OUT) next[key] = state.prefs[key];
  state = { prefs: next as unknown as Prefs, extra: {} };
  try {
    localStorage.setItem(PREFS_KEY, serializePrefs(state.prefs));
  } catch {
    // Сховище недоступне — у ньому нічого й немає.
  }
  sealed = true;
  applyToDocument(state.prefs);
  for (const l of listeners) l();
}

function writePrefs(patch: Partial<Prefs>) {
  if (sealed) return;
  state = { prefs: { ...state.prefs, ...patch }, extra: state.extra };
  try {
    localStorage.setItem(PREFS_KEY, serializePrefs(state.prefs, state.extra));
  } catch {
    // Приватний режим або переповнене сховище: налаштування діє до кінця вкладки.
  }
  applyToDocument(state.prefs);
  for (const l of listeners) l();
}

/** Для тестів: перечитати сховище. */
export function reloadPrefs() {
  sealed = false;
  state = read();
  for (const l of listeners) l();
}

// Зміна в іншій вкладці діє й тут одразу (UX-45, PRF-08: «у всіх вкладках»). Подію `storage`
// браузер шле лише іншим вкладкам того самого походження, тож власний запис сюди не повертається.
// `key === null` — інша вкладка очистила сховище.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key !== PREFS_KEY && e.key !== null) return;
    state = read();
    applyToDocument(state.prefs);
    for (const l of listeners) l();
  });
}

function subscribe(l: () => void) {
  listeners.add(l);
  return () => listeners.delete(l);
}

export function usePrefs(): Prefs {
  return useSyncExternalStore(subscribe, getPrefs, getPrefs);
}

/** Висота рядка списку для віртуалізатора: має збігатися з --cl-row-h (тест у styles). */
export const ROW_HEIGHT: Record<Density, number> = { comfortable: 40, compact: 32 };

/**
 * «Профіль можливостей», який читають компоненти (stack.md, «Слабкі ПК і легкий режим»):
 * що вимикає легкий режим — lite-mode.md, розд. 1.
 */
export interface Capabilities {
  lite: boolean;
  pageSize: 25 | 50;
  timelineLimit: 10 | 20;
  prefetch: boolean;
  rowHeight: number;
}

export function capabilitiesOf(p: Prefs): Capabilities {
  return {
    lite: p.lite,
    pageSize: p.lite ? 25 : 50,
    timelineLimit: p.lite ? 10 : 20,
    prefetch: !p.lite,
    rowHeight: ROW_HEIGHT[p.density],
  };
}

export function useCapabilities(): Capabilities {
  const p = usePrefs();
  return capabilitiesOf(p);
}
